import { css } from "@emotion/react";
import type { RelatedBundleElemT, SwarovskiOptikLocaleFrontend, TranslatedValue, TypedMoney } from "@swo/storefront";
import { useState } from "react";
import { Link } from "react-router";
import { IconMinus, IconPlus, NoImageSvg, PercentSvg } from "../../../assets";
import { ContentfulImage2 } from "../../../components/_shared-components/image-related/ContentfulImage2";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { useCtxFns, usePageContext } from "../../../global/page-context";
import { CSS_MQ } from "../../../styles/breakpoints";
import { FONTS, fontSize } from "../../../styles/fonts";
import { soListStyles } from "../../../styles/rich-text";
import { PDPHeadlineTypography } from "../pdp-headline-typography";
import { usePdpTranslations } from "../PdpTranslationsContext";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { T, TWrapper } from "../../../components/T";
import cn from "classnames";
import type { EnrichedSwarovskiProductVariantDiscountedPriceFrontend } from "@swo/shared";
import { hasDiscount } from "../../../domain/products";
import { formatStartingPriceWithTwoElements } from "../../../domain/swarovski-optik/products/price/format-price";
import { SwarovskiPriceTag } from "../../../components/_shared-components/ui-elements/SwarovskiTag";
import { SwarovskiInfoTooltip } from "../../../components/_shared-components/ui-elements/SwarovskiTooltip";
import { InterpolateLinearlyReact } from "../../../domain/translations/interpolate-linearly-react";

export const PDPBundleTeaser: React.FC<{ bundles: Array<RelatedBundleElemT> }> = ({ bundles }) => {
  const t = usePdpTranslations();
  const [limit, setLimit] = useState(3);
  const {
    generalTranslations,
    pageBase: { locale },
  } = usePageContext();
  const { showPrice } = useCtxFns();
  const fromPriceLabelTranslation = generalTranslations("fromPriceLabel");
  const lowestPriceLast30DaysTranslation = generalTranslations("lowestPriceLast30Days");

  if (bundles.length === 0) {
    return null;
  }

  return (
    <div className="js-pdp-bundle-teaser">
      <span
        css={[
          PDPHeadlineTypography,
          css`
            display: block;
            margin-top: 20px;
          `,
        ]}
      >
        <T>{t("bundleTeaser.headline")}</T>
      </span>

      <ul
        css={css`
          margin-top: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        {bundles.slice(0, limit).map(bundle => (
          <li key={JSON.stringify(bundle)}>
            <BundleTeaserItem
              bundle={bundle}
              fromPriceLabelTranslation={fromPriceLabelTranslation}
              lowestPriceLast30DaysTranslation={lowestPriceLast30DaysTranslation}
              localeForNumberFormat={locale}
            />
          </li>
        ))}
      </ul>

      {bundles.length > limit && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            margin-top: 20px;
          `}
        >
          <SwarovskiButton onClick={() => setLimit(l => l + 6)} variant="plain-underline" as="button">
            <T>{t("bundleTeaser.showMore")}</T>
          </SwarovskiButton>
        </div>
      )}
    </div>
  );
};

export const BundleTeaserItem: React.FC<{
  bundle: RelatedBundleElemT;
  fromPriceLabelTranslation: TranslatedValue;
  lowestPriceLast30DaysTranslation: TranslatedValue;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
}> = ({ bundle, fromPriceLabelTranslation, lowestPriceLast30DaysTranslation, localeForNumberFormat }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const mainBundleImage =
    bundle.images.thumbnail?.url ??
    bundle.images.gallery.flatMap(_ => (_.__typename === "UtilityImageWithFocalPoint" ? [_.image.url] : []))[0];

  const isDiscounted = hasDiscount(bundle.discountedStartingPrice);

  return (
    <div>
      <div
        css={css`
          position: relative;

          display: grid;

          --size: 40px;
          ${CSS_MQ.atLeastMd} {
            --size: 48px;
          }

          --image-size: 80px;
          ${CSS_MQ.atLeastMd} {
            --image-size: 110px;
          }

          grid-template-columns: var(--image-size) 1fr var(--size);

          gap: 16px;
        `}
      >
        <Link
          to={bundle.path}
          className={cn(mainBundleImage ? undefined : "no-image", "u-relative")}
          css={css`
            aspect-ratio: 1 / 1;
            display: flex;
            justify-content: center;

            &.no-image {
              background-color: ${CSS_COLORS.Gray2024};
            }
          `}
        >
          {isDiscounted && (
            <div
              css={css`
                position: absolute;
                top: 0;
                right: 0;

                width: 32px;
                height: 32px;

                z-index: 1;

                background-color: ${CSS_COLORS.Green};
                color: ${CSS_COLORS.White};

                display: flex;
                align-items: center;
                justify-content: center;

                svg {
                  width: 16px;
                  height: 16px;
                }
              `}
            >
              <PercentSvg />
            </div>
          )}
          {mainBundleImage ? (
            <ContentfulImage2
              assetId={undefined}
              contentfulUrl={mainBundleImage}
              sizes="(max-width: 768px) 80px, 110px"
              css={css`
                height: 80px;
                width: 80px;

                ${CSS_MQ.atLeastMd} {
                  height: 110px;
                  width: 110px;
                }

                object-fit: cover;
                object-position: center;
              `}
              alt={bundle.name}
              withTransparency
              cropping={undefined}
            />
          ) : (
            <div
              css={css`
                display: flex;
                align-items: center;
                align-self: center;
                justify-items: center;
              `}
            >
              <NoImageSvg
                strokeWidth={3}
                css={css`
                  width: 40px;
                  height: 40px;
                  color: ${CSS_COLORS.GrayDark2024};
                `}
              />
            </div>
          )}
        </Link>

        <Link
          to={bundle.path}
          css={css`
            padding-top: 12px;
            display: flex;
            flex-direction: column;
            gap: 12px;

            text-decoration: none;
            color: inherit;

            &:hover {
              ._headline {
                text-decoration: underline;
              }
            }
          `}
        >
          <span className="_headline" css={[FONTS.SwarovskiFutura500, fontSize.x25]}>
            {bundle.name}
          </span>

          {bundle.startingPrice && (
            <BundlePrice
              price={bundle.startingPrice}
              discountedPrice={bundle.discountedStartingPrice}
              discountPercentage={bundle.discountPercentage}
              lowestPrice30Days={bundle.lowestPrice30Days}
              fromPriceLabelTranslation={fromPriceLabelTranslation}
              lowestPriceLast30DaysTranslation={lowestPriceLast30DaysTranslation}
              localeForNumberFormat={localeForNumberFormat}
            />
          )}
        </Link>

        <button
          css={css`
            position: absolute;
            right: 0;
            top: calc(50% - var(--size) / 2);

            svg {
              width: var(--size);
              height: var(--size);
            }
          `}
          onClick={e => {
            e.preventDefault();
            setIsExpanded(_ => !_);
          }}
        >
          {isExpanded ? <IconMinus /> : <IconPlus />}
        </button>
      </div>

      {isExpanded ? (
        <ul
          css={[
            soListStyles,
            css`
              margin-top: 12px;

              margin-left: 12px;
              margin-bottom: 8px;

              ${CSS_MQ.atLeastMd} {
                margin-left: 12px;
                margin-bottom: 12px;
              }

              li + li {
                margin-top: 4px;
              }
            `,
          ]}
        >
          {bundle.products.map(product => (
            <li key={product.id} css={[FONTS.SwarovskiFutura300, fontSize.x2]}>
              {product.name}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const BundlePrice: React.FC<{
  price: TypedMoney;
  discountedPrice?: EnrichedSwarovskiProductVariantDiscountedPriceFrontend;
  discountPercentage: number | undefined;
  lowestPrice30Days: TypedMoney | undefined;

  fromPriceLabelTranslation: TranslatedValue;
  lowestPriceLast30DaysTranslation: TranslatedValue;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
}> = ({
  price,
  discountedPrice,
  discountPercentage,
  lowestPrice30Days,

  fromPriceLabelTranslation,
  lowestPriceLast30DaysTranslation,
  localeForNumberFormat,
}) => {
  const [first, second, last, lowestPrice, discountPercentageString] = formatStartingPriceWithTwoElements(
    { startingPrice: price, discountedStartingPrice: discountedPrice, discountPercentage, lowestPrice30Days },
    fromPriceLabelTranslation,
    localeForNumberFormat,
    true,
  );

  const tooltip =
    last && lowestPrice ? (
      <SwarovskiInfoTooltip
        content={
          <TWrapper forcedValue={lowestPriceLast30DaysTranslation}>
            <InterpolateLinearlyReact
              translationStringWithInterpolatedValues={lowestPriceLast30DaysTranslation.value}
              args={[lowestPrice]}
            />
          </TWrapper>
        }
      />
    ) : undefined;

  const priceTagValue = last && (discountPercentageString || last);

  return (
    <div
      className={"u-d-flex"}
      css={css`
        gap: 8px;
        align-items: center;
      `}
    >
      <span suppressHydrationWarning css={[FONTS.SwarovskiFutura300, fontSize.x25]} className="u-fw-500">
        {first} {second}
      </span>
      {priceTagValue && <SwarovskiPriceTag label={priceTagValue}>{priceTagValue}</SwarovskiPriceTag>}
      {tooltip}
    </div>
  );
};
